import type { Tone } from '../../../util/term'

/**
 * Inline SVG mark — renders behind the score number in a cell. Layered
 * for the extreme tiers (albatross / triple) where an outer hairline
 * stroke at the cell's edge surrounds an inset filled shape.
 *
 * Seven-tier mark grammar:
 *   Albatross (≤ par − 3) → filled gold circle + offset outline
 *   Eagle     (par − 2)   → filled gold circle
 *   Birdie    (par − 1)   → hollow circle, flag-red-dark stroke
 *   Par                   → no mark
 *   Bogey     (par + 1)   → hollow square, ink-700 stroke
 *   Double    (par + 2)   → filled ink-900 square
 *   Triple    (≥ par + 3) → filled ink-900 square + offset outline
 *
 * Circle = under par, square = over par. Intensity ramps hollow →
 * filled → filled-with-offset-outline as you move away from par.
 */
const ScoreMark = ({ tone, size }: { tone: Tone; size: number }) => {
  if (tone === 'par') return null
  const outerInset = 1
  const innerInset = 4
  const outerDim = size - outerInset * 2
  const innerDim = size - innerInset * 2
  const c = size / 2
  switch (tone) {
    case 'albatross':
      return (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          {/* Outer hairline ring — sits ~3px outside the filled disc */}
          <circle
            cx={c}
            cy={c}
            r={outerDim / 2}
            fill="none"
            stroke="var(--gold)"
            strokeWidth={1.25}
          />
          <circle cx={c} cy={c} r={innerDim / 2} fill="var(--gold)" />
        </svg>
      )
    case 'eagle':
      return (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          <circle cx={c} cy={c} r={innerDim / 2} fill="var(--gold)" />
        </svg>
      )
    case 'birdie':
      return (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          <circle
            cx={c}
            cy={c}
            r={innerDim / 2}
            fill="none"
            stroke="var(--flag-red-dark)"
            strokeWidth={2}
          />
        </svg>
      )
    case 'bogey':
      return (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          <rect
            x={innerInset}
            y={innerInset}
            width={innerDim}
            height={innerDim}
            rx={2}
            fill="none"
            stroke="var(--ink-700)"
            strokeWidth={2}
            strokeLinejoin="round"
          />
        </svg>
      )
    case 'double':
      return (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          <rect
            x={innerInset}
            y={innerInset}
            width={innerDim}
            height={innerDim}
            rx={2}
            fill="var(--ink-900)"
          />
        </svg>
      )
    case 'triple':
      return (
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="absolute inset-0 pointer-events-none"
          aria-hidden
        >
          {/* Outer hairline frame — broadcast convention for +3+ */}
          <rect
            x={outerInset}
            y={outerInset}
            width={outerDim}
            height={outerDim}
            rx={2}
            fill="none"
            stroke="var(--ink-900)"
            strokeWidth={1.25}
            strokeLinejoin="round"
          />
          <rect
            x={innerInset}
            y={innerInset}
            width={innerDim}
            height={innerDim}
            rx={2}
            fill="var(--ink-900)"
          />
        </svg>
      )
    default:
      return null
  }
}

export default ScoreMark
